import {
  BackendContent,
  BackendPartner,
  BackendUser,
  Content,
  CursorElement,
  PostTypes,
  UserFunction
} from 'common/types'
import { useMentions } from './useMentions'
import { useEffect, useReducer, useRef, useState } from 'react'
import { IMentionsState } from 'common/types/mentions.types'
import { createMentionsState } from 'common/utils/createMentionsState'
import { getHTMLTextLength } from 'common/utils/getHTMLTextLength'
import { removeHashtagLinks } from 'common/utils/removeHashtagLinks'
import { extractHashtags } from 'common/utils/addHashtagLinks'
import { getMentionsTags } from 'common/utils/getMentionsTags'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useNotification } from 'hooks/use-notification/useNotification'
import { findHashtag } from 'common/utils/findHashtag'
import { makeStyles } from '@material-ui/core'
import { useLocation } from 'react-router-dom'
const initialDescriptionState = createMentionsState('description-type')

export const useCreatePostModal = (
  backendUserFunctions: UserFunction[],
  postAsPartner: boolean,
  backendUser: BackendUser,
  postToEdit: Content,
  backendPartner: BackendPartner,
  postAsMember: boolean,
  getPostDetails: Function,
  isVideo: Function,
  setPreviewVideo: Function,
  setPreviewImage: Function,
  setVideoUploaded: Function,
  isImage: Function,
  setPostImages: Function,
  setImageUploaded: Function,
  show: boolean,
  picturesChanged: Function,
  previewImage: string[],
  previewVideo: string[],
  maxTitleLength: number,
  maxDescriptionLength: number,
  postImages: string[],
  newUploads: boolean,
  imageUploaded: boolean,
  videoUploaded: boolean,
  media: any,
  previewMedia: any,
  mediaCategory: any,
  postVideo: any,
  post: any,
  edit: boolean,
  postId: String,
  updateContent: Function,
  useFormatHours: Function,
  updateContentActivity: Function,
  createContent: Function,
  posts: any,
  hideFeed: boolean,
  setPosts: Function,
  setPost: Function,
  setShowLoader: Function,
  myPosts: any,
  setMyPosts: Function,
  setShow: Function,
  clearPost: Function,
  setCursorPos: Function,
  setCursorEl: Function,
  setPostTitle: Function,
  setPostDescription: Function,
  cursorEl: any,
  theme: any
) => {
  const {
    getResults,
    users,
    setUsers,
    setMentions,
    mentions,
    totalSuggestions,
    tags,
    setTags,
    setTagResults,
    tagResults,
    searchText
  } = useMentions()
  const { logEventAnalytics } = useAnalytics()
  const { notify } = useNotification()
  const location = useLocation()
  // @ts-ignore
  const uuid = '' as string
  const [titleTextfieldValue, setTitleTextfieldValue] = useState('')
  const [textfieldCharacterLength, setTextfieldCharacterLength] = useState(0)
  const postPermission = backendUserFunctions?.find(x => x?.name === 'Create Post')
  const [postAs, setPostAs] = useState<string>()
  const userHeadline = postAsPartner
    ? undefined
    : backendUser?.onboardingQuestions?.find(x => x.question === 'Headline')?.answer
  const titleTextfieldId = `title-type-${uuid}`
  const [mentionedCounter, setMentionedCounter] = useState(0)
  const resetMentioningRef = useRef(() => {})
  const descriptionResetMentioningRef = useRef(() => {})
  const [isMentioning, setIsMentioning] = useState(false)
  const [descriptionMentionsState, descriptionStateDispatch] = useReducer<
    (state: IMentionsState, action: Partial<IMentionsState>) => IMentionsState
  >(
    (state, action) => {
      return { ...state, ...action }
    },
    { ...initialDescriptionState, textfieldId: `${initialDescriptionState.textfieldId}-${uuid}` }
  )
  const [isPostingContent, setIsPostingContent] = useState<boolean>(false)

  const determinePoster = () => {
    if (postToEdit) {
      setPostAs(postToEdit.partnerId ? 'Partner' : 'Member')
    } else if (!backendPartner || !postPermission || postAsMember) {
      setPostAs('Member')
    } else if (postAsPartner) {
      setPostAs('Partner')
    } else {
      setPostAs('')
    }
  }

  useEffect(() => {
    if (edit) {
      if (postToEdit?.type === 'share' || postToEdit?.type === 'activity') {
        const content = postToEdit?.content[0]?.comment as string

        const textfieldCharacterLength = getHTMLTextLength(content)

        descriptionStateDispatch({
          textfieldValue: content,
          textfieldCharacterLength
        })
      } else {
        const _formattedContent = getPostDetails(postToEdit?.content) || ''

        const textfieldCharacterLength = getHTMLTextLength(_formattedContent)
        const titleCharCount = getHTMLTextLength(postToEdit?.contentTitle)

        const formattedContent = removeHashtagLinks(_formattedContent)

        descriptionStateDispatch({
          textfieldValue: formattedContent,
          textfieldCharacterLength
        })
        setTitleTextfieldValue(postToEdit?.contentTitle)
        setTextfieldCharacterLength(titleCharCount)

        if (postToEdit?.media?.length > 0 && isVideo(postToEdit?.media)) {
          setPreviewVideo([...postToEdit?.media])
          setVideoUploaded(true)
        }
        if (postToEdit?.media?.length > 0 && isImage(postToEdit?.media)) {
          setPreviewImage([...postToEdit?.media])
          setPostImages([...postToEdit?.media])
          setImageUploaded(true)
        }
      }
    }
    determinePoster()
  }, [show])

  const changesMade = () => {
    let noChangesMade =
      (postToEdit?.contentTitle === titleTextfieldValue &&
        getPostDetails(postToEdit?.content) === descriptionMentionsState.textfieldValue &&
        picturesChanged(postToEdit?.media, previewImage) &&
        isImage(postToEdit?.media) &&
        postToEdit?.media?.length === previewImage?.length) ||
      (isVideo(postToEdit?.media) && postToEdit?.media?.length === previewImage?.length) ||
      (postToEdit?.media?.length === previewImage?.length && postToEdit?.media?.length === previewVideo?.length)

    const checkTitleLengthMax = textfieldCharacterLength > maxTitleLength
    const checkDescriptionLengthMax = descriptionMentionsState.textfieldCharacterLength > maxDescriptionLength
    const titleEmpty = !textfieldCharacterLength
    const descriptionEmpty = !descriptionMentionsState.textfieldCharacterLength
    let bothTitleAndDescriptionEmpty = titleEmpty && descriptionEmpty

    let characterLengthCheck = bothTitleAndDescriptionEmpty || checkTitleLengthMax || checkDescriptionLengthMax

    if (edit && (postToEdit?.type === 'share' || postToEdit?.type === 'activity')) {
      noChangesMade = postToEdit?.content[0]?.comment?.length === descriptionMentionsState.textfieldCharacterLength
      characterLengthCheck = checkDescriptionLengthMax
    }

    if (characterLengthCheck) {
      return true
    } else if (noChangesMade) {
      return true
    } else if (postAs === '') {
      return true
    } else {
      return (
        !textfieldCharacterLength &&
        !descriptionMentionsState.textfieldCharacterLength &&
        previewImage.length === 0 &&
        previewVideo.length === 0
      )
    }
  }

  const isMediaUnedited = () => {
    if (isImage(postToEdit?.media)) {
      return (
        postToEdit?.media?.length === postImages?.length &&
        postToEdit?.media[0].localeCompare(postImages[0]) === 0 &&
        !newUploads
      )
    }
    if (isVideo(postToEdit?.media)) {
      return (
        postToEdit?.media?.length === previewVideo?.length &&
        postToEdit?.media[0].localeCompare(previewVideo[0]) === 0 &&
        !newUploads
      )
    }
  }

  const handlePost = async (userId: string) => {
    const title = titleTextfieldValue
    const description = descriptionMentionsState.textfieldValue

    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = backendUser?.id
    const hashTags = extractHashtags(description)
    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
      partnerLog['Selected Profile'] = postAs
      hashTags?.length > 0 && (partnerLog['Hastags added'] = hashTags)
    } else {
      userLog['Account Type'] = backendUser?.role
      hashTags?.length > 0 && (userLog['Hastags added'] = hashTags)
    }
    if (imageUploaded) {
      media[0] = JSON.stringify(postImages)
      previewMedia[0] = previewImage
      mediaCategory[0] = 'image'
      logEventAnalytics('Member', 'Upload picture in post', 'GA')
    } else if (videoUploaded) {
      media[0] = JSON.stringify(postVideo)
      previewMedia[0] = previewVideo
      mediaCategory[0] = 'video'
      logEventAnalytics('Member', 'Upload video in post', 'GA')
    } else {
      media[0] = null
      previewMedia[0] = null
      logEventAnalytics('Member', 'Create Post (Text Only)', 'GA')
    }

    const { mentioned, tagged, html: descriptionHTML } = getMentionsTags({
      mentions,
      mentionedClassIdentifier: ['.title-mentioned', '.description-mentioned'],
      tags,
      html: description,
      inputElement: document.querySelector(`.${descriptionMentionsState?.textfieldId}`)
    })

    isMediaUnedited()
      ? (post[0] = {
          title,
          mentions: mentioned,
          tags: tagged,
          content: JSON.stringify({ body: descriptionHTML }),
          type: `${postToEdit?.type === 'share' ? PostTypes.SHARE : PostTypes.ARTICLE}`,
          partnerId: postAsPartner || postAs === 'Partner' ? backendPartner?.id : null
        })
      : (post[0] = {
          title,
          mentions: mentioned,
          tags: tagged,
          content: JSON.stringify({ body: descriptionHTML }),
          media: media[0],
          type: `${postToEdit?.type ? postToEdit?.type : PostTypes.ARTICLE}`,
          partnerId: postAsPartner || postAs === 'Partner' ? backendPartner?.id : null
        })

    let fePost: BackendContent
    if (edit) {
      switch (postToEdit?.type) {
        case 'article':
          fePost = await updateContent(userId, postId, post[0])
          logEventAnalytics(
            backendUser?.partnerId ? 'Partner' : backendUser?.role,
            'Post edited',
            'both',
            'Edit Post',
            {
              'Post ID': postId,
              'Time after post': useFormatHours(
                (new Date(fePost?.updatedAt).getTime() - new Date(fePost?.createdAt).getTime()) / (1000 * 3600)
              ),
              ...partnerLog,
              ...userLog
            },
            userId
          )
          break
        case 'share':
          fePost = await updateContentActivity(userId, postToEdit?.id, description)
          logEventAnalytics(
            backendUser?.partnerId ? 'Partner' : backendUser?.role,
            'Post edited',
            'both',
            'Edit Post',
            {
              'Post ID': postId,
              'Time after post': useFormatHours(
                (new Date(fePost?.updatedAt).getTime() - new Date(fePost?.createdAt).getTime()) / (1000 * 3600)
              ),
              ...partnerLog,
              ...userLog
            },
            userId
          )
          break
        case 'activity':
          fePost = await updateContentActivity(userId, postToEdit?.id, description)
          break
      }

      fePost && setIsPostingContent(false)
    } else {
      fePost = await createContent(userId, post[0])
      if (fePost) {
        setIsPostingContent(false)
        notify('success', 'Posted Successfully')
        isVideo(fePost?.media) && backendUser?.partnerId && (partnerLog['Video added'] = fePost?.media[0])
        isVideo(fePost?.media) && !backendUser?.partnerId && (userLog['Video added'] = fePost?.media[0])
        isImage(fePost?.media) && backendUser?.partnerId && (partnerLog['Image added'] = fePost?.media[0])
        isImage(fePost?.media) && !backendUser?.partnerId && (userLog['Image added'] = fePost?.media[0])

        logEventAnalytics(
          backendUser?.partnerId ? 'Partner' : backendUser?.role,
          location.pathname === '/profile' ? 'Post created on My Page' : 'Post created',
          'mixpanel',
          'User publishes their post',
          { 'Post ID': fePost?.id, ...partnerLog, ...userLog },
          userId
        )
      }
    }

    const feCreatedPost: Content = {
      id: fePost?.type === 'share' ? fePost?.contentId : fePost?.id,
      type: fePost?.type,
      key: fePost?.id,
      contentTitle: fePost?.title,
      content: fePost?.type === 'share' ? [{ comment: description }, postToEdit?.content[1]] : fePost?.content,
      media: fePost?.media,
      userId: backendUser?.id,
      firstName: backendUser?.firstName,
      lastName: backendUser?.lastName,
      image: fePost?.partnerId ? backendPartner?.logo : backendUser?.image,
      connectionTitle: backendUser?.onboardingQuestions?.find(question => question.question === 'Headline')?.answer,
      createdAt: fePost?.createdAt,
      updatedAt: fePost?.updatedAt,
      partnerId: fePost?.partnerId,
      companyName: postAsPartner || postAs === 'Partner' ? backendPartner?.name : null
    }

    if (edit) {
      const postIndex = posts?.findIndex(post => post?.id === postToEdit?.id)
      const updatedPosts = [...posts]
      updatedPosts[postIndex] = feCreatedPost
      hideFeed ? setPost(feCreatedPost) : setPosts(updatedPosts)
    } else if (posts) {
      setShowLoader(false)
      posts?.unshift(feCreatedPost)
      setPosts([...posts])
      if (myPosts && myPosts.length) setMyPosts([feCreatedPost, ...myPosts])
    }

    setShow(false)
    clearLocalData()
    clearPost()
    setPostAs('')
  }

  const addHash = (id: string) => {
    const selection = window.getSelection()

    const textfield = document.querySelector(`.${id}`)

    if (!selection.rangeCount) {
      return false
    }

    selection.deleteFromDocument()
    const currentRange = selection.getRangeAt(0)

    if (textfield?.contains(currentRange.endContainer)) {
      currentRange.deleteContents()
      currentRange.insertNode(document.createTextNode('#'))
      currentRange.collapse(false)
      const html = textfield.innerHTML

      switch (id) {
        case titleTextfieldId: {
          setTitleTextfieldValue(html)
          break
        }
        case descriptionMentionsState.textfieldId: {
          getResults('', false, false)

          const range = document.createRange()

          const element = textfield

          if (element) {
            const startElement = currentRange.startContainer
            const startOffset = currentRange.startOffset

            range.setStart(element.firstChild, 0)
            range.setEnd(startElement, startOffset)

            const hashCount = Math.max(
              0,
              range
                ?.toString()
                ?.split('')
                ?.filter(val => val === '#').length
            )

            const triggerIndex = findHashtag(hashCount, html)

            descriptionStateDispatch({
              textfieldValue: html,
              isTagging: true,
              isMentioning: true,
              mentionedCounter: 0,
              hashCount,
              triggerIndex,
              addHashButtonTriggered: true
            })
          }
          break
        }
      }
    }
  }

  const handleChange = (e: any, type: string) => {
    switch (type) {
      case 'title': {
        setCursorPos(e?.target?.selectionStart)
        setCursorEl('title')
        if (e?.target?.value?.length <= maxTitleLength) {
          setPostTitle(e?.target?.value)
        } else {
          setPostTitle(e?.target?.value?.slice(0, maxTitleLength))
          return false
        }
        return
      }
      case 'description': {
        setCursorPos(e?.target?.selectionStart)
        setCursorEl('description')
        if (e?.target?.value?.length <= maxDescriptionLength) {
          setPostDescription(e?.target?.value)
        } else {
          setPostDescription(e?.target?.value?.slice(0, maxDescriptionLength))
        }
        return
      }
      case 'tag': {
        if (cursorEl === CursorElement.TITLE) {
          addHash(titleTextfieldId)
        }

        if (cursorEl === CursorElement.DESCRIPTION) {
          addHash(descriptionMentionsState?.textfieldId)
        }
        return
      }
      case 'image': {
        setImageUploaded(true)
        setVideoUploaded(false)
        return
      }
      case 'video': {
        setVideoUploaded(true)
        setImageUploaded(false)
        return
      }
      case 'profile': {
        setPostAs(e?.target?.value)
      }
    }
  }

  const renderStaticUserInfo = () => {
    if (postAsMember || !backendPartner) {
      return true
    } else if (backendPartner && postAsPartner) {
      return true
    } else if (backendPartner && !postPermission) {
      return true
    } else if (edit) {
      return true
    }
    return false
  }

  const clearLocalData = () => {
    try {
      setUsers([])
      setTitleTextfieldValue('')
      setTextfieldCharacterLength(0)
      descriptionStateDispatch(initialDescriptionState)
    } catch (err) {}
  }

  const useStyles = makeStyles(() => ({
    select: {
      width: '15em',
      minWidth: '0px !important',
      top: '30% !important',
      left: '35% !important',
      right: '0 !important'
    },
    dropDown: {
      color: theme.colors.primary,
      margin: '0 0.375rem'
    }
  }))
  const classes = useStyles()

  useEffect(() => {
    const count = getHTMLTextLength(titleTextfieldValue)

    setTextfieldCharacterLength(count)
  }, [titleTextfieldValue])

  useEffect(() => {
    const count = getHTMLTextLength(descriptionMentionsState.textfieldValue)

    descriptionStateDispatch({
      textfieldCharacterLength: count
    })
  }, [descriptionMentionsState.textfieldValue])
  return {
    isPostingContent,
    clearLocalData,
    setPostAs,
    renderStaticUserInfo,
    userHeadline,
    postAs,
    postPermission,
    handleChange,
    classes,
    getResults,
    setMentions,
    setUsers,
    titleTextfieldId,
    setTextfieldCharacterLength,
    users,
    setTitleTextfieldValue,
    totalSuggestions,
    mentionedCounter,
    setMentionedCounter,
    resetMentioningRef,
    isMentioning,
    setIsMentioning,
    titleTextfieldValue,
    mentions,
    tags,
    textfieldCharacterLength,
    descriptionMentionsState,
    descriptionStateDispatch,
    setTagResults,
    descriptionResetMentioningRef,
    changesMade,
    handlePost,
    setIsPostingContent,
    tagResults,
    searchText,
    setTags
  } as const
}
